import styled, { createGlobalStyle } from 'styled-components';

/**
 * Design tokens ported from the approved prototype (V4-Program-Setup-Prototype.html /
 * V4-Program-Setup-Console.html). Scoped to `.ps-redesign` so nothing bleeds into the
 * rest of the app (TV's ProgramSetup module included) — every rule below lives under
 * that root class.
 */
export const PsGlobalStyle = createGlobalStyle`
  .ps-redesign {
    --ink:#16203f; --ink-2:#4a5573; --ink-3:#7a849e; --ink-4:#9aa3b8;
    --line:#e3e8f0; --line-2:#eef1f6;
    --surface:#ffffff; --ground:#f4f7fb; --raise:#f9fafd; --field:#ffffff;
    --accent:#2f37bd; --accent-2:#3b45d4; --accent-soft:#e8effd; --accent-ink:#1d4ed8;
    --focus:#2563eb;
    --chip:#eef1f7; --chip-line:#dfe4ee; --chip-ink:#38415e;
    --req:#ef4444; --req-soft:#fdeded;
    --ok:#22c55e; --ok-soft:#e4f8ec; --ok-ink:#15803d;
    --warn:#b45309; --warn-soft:#fdf2e3;
    --ic-doc-bg:#e6effd; --ic-doc-fg:#2f6fed;
    --ic-ppl-bg:#ecebfd; --ic-ppl-fg:#5b52e0;
    --ic-rec-bg:#d8f4ea; --ic-rec-fg:#0f9d6e;
    --ic-time-bg:#e5e9fe; --ic-time-fg:#4a52d6;
    --shadow:0 1px 2px rgba(20,26,60,.05), 0 8px 24px -12px rgba(20,26,60,.16);
    --shadow-pop:0 12px 40px -8px rgba(20,26,60,.28);
    --r:12px;

    font-family:"Figtree",-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;
    font-size:14px; line-height:1.45; color:var(--ink);
  }
  .ps-redesign *{box-sizing:border-box}

  /* AntD ships its own explicit font-family on nearly every component class
     (button, table, select, pagination, modal…), which wins over inheriting
     from .ps-redesign since a direct rule always beats an inherited value —
     force it everywhere in scope, then let .ps-mono win back on specificity
     (two class selectors beat one, even with matching !important). */
  .ps-redesign, .ps-redesign *{
    font-family:"Figtree",-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif !important;
  }
  .ps-redesign .ps-mono, .ps-redesign .ps-mono *{
    font-family:"IBM Plex Mono",monospace !important;
  }

  /* Re-skin the shared AntD-backed field components only within this page. */
  .ps-redesign .ant-select-selector,
  .ps-redesign input.ant-input,
  .ps-redesign .ant-picker,
  .ps-redesign .ant-input-number{
    border-radius:9px !important; border-color:var(--line) !important;
    background:var(--field) !important; min-height:42px !important;
  }
  .ps-redesign .ant-select-focused .ant-select-selector,
  .ps-redesign input.ant-input:focus,
  .ps-redesign .ant-picker-focused,
  .ps-redesign .ant-input-number-focused{
    border-color:var(--focus) !important; box-shadow:0 0 0 3px var(--accent-soft) !important;
  }

  /* The taller single selects keep AntD's 30px line-height, so re-centre the value, placeholder and search box. */
  .ps-redesign .ant-select-single:not(.ant-select-customize-input) .ant-select-selector{
    height:42px !important; align-items:center;
  }
  .ps-redesign .ant-select-single .ant-select-selection-item,
  .ps-redesign .ant-select-single .ant-select-selection-placeholder{
    line-height:40px !important;
  }
  .ps-redesign .ant-select-single .ant-select-selection-search-input{ height:40px !important }

  /* Buttons, cards, modals: match the prototype's rounder, softer chrome. */
  .ps-redesign .ant-btn{
    border-radius:9px !important; font-weight:600 !important; box-shadow:none !important;
  }
  .ps-redesign .ant-card{
    border-radius:var(--r) !important; border-color:var(--line) !important; box-shadow:var(--shadow) !important;
  }
  .ps-redesign .ant-modal-content{ border-radius:var(--r) !important; overflow:hidden }
  .ps-redesign .ant-dropdown-menu{ border-radius:11px !important; box-shadow:var(--shadow-pop) !important }
  .ps-redesign .ant-checkbox-inner{ border-radius:4px !important }
  .ps-redesign .ant-checkbox-checked .ant-checkbox-inner{ background-color:var(--accent) !important; border-color:var(--accent) !important }

  /* List/table: reuse the existing AntD Table + pagination, just re-themed. */
  .ps-redesign .ant-table{ background:var(--surface); font-size:13.5px }
  .ps-redesign .ant-table-thead > tr > th{
    background:var(--raise) !important; color:var(--ink-4) !important;
    font-size:10.5px; font-weight:700; letter-spacing:.09em; text-transform:uppercase;
    border-bottom:1px solid var(--line) !important;
  }
  .ps-redesign .ant-table-tbody > tr > td{ border-bottom:1px solid var(--line-2) !important }
  .ps-redesign .ant-table-tbody > tr:hover > td{ background:var(--raise) !important }
  .ps-redesign .ant-pagination-item{
    border-radius:6px !important; border-color:var(--line) !important; min-width:28px !important; height:28px !important;
  }
  .ps-redesign .ant-pagination-item a{ color:var(--ink-2) !important }
  .ps-redesign .ant-pagination-item-active{ border-color:var(--accent) !important; background:var(--accent) !important }
  .ps-redesign .ant-pagination-item-active a{ color:#fff !important }
  .ps-redesign .ant-pagination-prev .ant-pagination-item-link,
  .ps-redesign .ant-pagination-next .ant-pagination-item-link{
    border-radius:6px !important; border-color:var(--line) !important;
  }
  .ps-redesign .ps-pill{
    display:inline-block; padding:3px 10px; border-radius:999px; font-size:11.5px; font-weight:700;
    background:var(--ok-soft); color:var(--ok-ink);
  }
  .ps-redesign .ps-pill.off{ background:var(--ground); color:var(--ink-3) }
  .ps-redesign .ps-linkish{
    background:none; border:0; padding:0; font-size:13.5px; font-weight:600; color:var(--accent-ink);
    cursor:pointer; text-align:left;
  }
  .ps-redesign .ps-linkish:hover{ text-decoration:underline }
`;

/* ---------- list page (matches the prototype's .page / .listbar / .card) ---------- */
// Sits on the app layout's own background, like the TV Ads page (AdsList.styled PageWrapper).
export const ListPage = styled.div`
  min-height: 100%;
  margin-top: -6px;
`;

export const ListBar = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

export const FilterFld = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 0 1 280px;
  min-width: 0;

  @media (max-width: 860px) {
    flex: 1 1 100%;
  }
`;

export const FilterLabel = styled.label`
  font-size: 12.5px;
  font-weight: 600;
  color: var(--ink-2);
`;

export const TableCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r);
  box-shadow: var(--shadow);
  overflow: hidden;

  .ant-table {
    border-radius: 0;
  }
  .ant-table-thead > tr > th {
    padding: 11px 18px;
    white-space: nowrap;
  }
  .ant-table-tbody > tr > td {
    padding: 12px 18px;
    vertical-align: middle;
    font-size: 13.5px;
  }
  .ant-table-pagination.ant-pagination {
    margin: 0;
    padding: 11px 18px;
    align-items: center;
  }
  .ant-pagination-total-text {
    font-size: 12.5px;
    color: var(--ink-3);
  }
`;

/* ---------- drawer (slide-over) ---------- */
export const DrawerWrap = styled.div<{ $open?: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 200;
`;

export const DrawerScrim = styled.div<{ $open?: boolean }>`
  position: absolute;
  inset: 0;
  background: rgba(12, 16, 38, 0.45);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transition: opacity 0.28s ease;
`;

export const Drawer = styled.div<{ $open?: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(1120px, 94vw);
  background: var(--ground);
  box-shadow: -24px 0 70px -24px rgba(12, 16, 38, 0.5);
  display: flex;
  flex-direction: column;
  transform: translateX(${({ $open }) => ($open ? '0' : '100%')});
  transition: transform 0.3s cubic-bezier(0.22, 0.72, 0.3, 1);

  @media (max-width: 980px) {
    width: 100%;
  }
`;

export const DrawerHeadWrap = styled.header`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  flex: none;
  padding: 14px 24px;
  background: var(--surface);
  border-bottom: 1px solid var(--line);
`;

export const DrawerEyebrow = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.11em;
  color: var(--ink-4);
  text-transform: uppercase;
`;

export const DrawerTitleText = styled.div`
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.02em;
  margin-top: 3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const DrawerActs = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
`;

export const DrawerX = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 9px;
  border: 1px solid var(--line);
  background: var(--surface);
  color: var(--ink-3);
  cursor: pointer;
  display: grid;
  place-items: center;
  flex: none;

  &:hover {
    background: var(--ground);
    color: var(--ink);
  }
`;

export const DrawerBody = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 20px 24px 40px;
`;

/* ---------- delete confirmation modal ---------- */
export const ModalWrap = styled.div`
  position: fixed;
  inset: 0;
  z-index: 300;
  background: rgba(12, 16, 38, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

export const ModalCard = styled.div`
  width: 100%;
  max-width: 380px;
  background: var(--surface);
  border-radius: 14px;
  box-shadow: var(--shadow-pop);
  padding: 24px;
  text-align: center;
`;

export const ModalIc = styled.div`
  width: 46px;
  height: 46px;
  margin: 0 auto 12px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: var(--req-soft);
  color: var(--req);

  svg {
    width: 22px;
    height: 22px;
  }
`;

export const ModalTitle = styled.div`
  font-size: 17px;
  font-weight: 700;
  letter-spacing: -0.01em;
`;

export const ModalBody = styled.div`
  font-size: 13.5px;
  color: var(--ink-3);
  margin-top: 6px;
  line-height: 1.5;
`;

export const ModalFoot = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;

  button {
    flex: 1;
  }
`;

export const ProgramSetupContainer = styled.div`
  &.ps-redesign {
    display: flex;
    flex-direction: column;
    gap: 18px;
    padding-bottom: 40px;
  }
`;

/* ---------- sticky action bar ---------- */
export const ActionBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  padding: 12px 0;
  background: var(--ground);
`;

export const DirtyPill = styled.span<{ $on?: boolean }>`
  display: ${({ $on }) => ($on ? 'inline-flex' : 'none')};
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: var(--warn);
  background: var(--warn-soft);
  padding: 5px 11px;
  border-radius: 999px;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }
`;

/* ---------- step rail ---------- */
export const Rail = styled.nav`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  padding: 10px 14px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r);
  box-shadow: var(--shadow);
`;

export const RailStep = styled.button<{ $on?: boolean }>`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 5px 10px;
  border-radius: 8px;
  font-size: 12.5px;
  font-weight: 600;
  border: 0;
  cursor: pointer;
  color: ${({ $on }) => ($on ? 'var(--accent-ink)' : 'var(--ink-3)')};
  background: ${({ $on }) => ($on ? 'var(--accent-soft)' : 'none')};

  &:hover {
    background: var(--ground);
    color: var(--ink);
  }

  .n {
    width: 19px;
    height: 19px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    flex: none;
    font-size: 10.5px;
    font-family: 'IBM Plex Mono', monospace !important;
    background: ${({ $on }) => ($on ? 'var(--accent)' : 'var(--ground)')};
    border: 1px solid ${({ $on }) => ($on ? 'var(--accent)' : 'var(--line)')};
    color: ${({ $on }) => ($on ? '#fff' : 'var(--ink-3)')};
  }
`;

/* ---------- cards ---------- */
export const Card = styled.section`
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r);
  box-shadow: var(--shadow);
  scroll-margin-top: 84px;
`;

export const CardHead = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 13px;
  padding: 18px 22px;
  flex-wrap: wrap;
`;

export const CardIc = styled.div<{ $tone?: 'ppl' | 'rec' | 'time' }>`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  flex: none;
  display: grid;
  place-items: center;
  background: ${({ $tone }) =>
    $tone === 'ppl'
      ? 'var(--ic-ppl-bg)'
      : $tone === 'rec'
      ? 'var(--ic-rec-bg)'
      : $tone === 'time'
      ? 'var(--ic-time-bg)'
      : 'var(--ic-doc-bg)'};
  color: ${({ $tone }) =>
    $tone === 'ppl'
      ? 'var(--ic-ppl-fg)'
      : $tone === 'rec'
      ? 'var(--ic-rec-fg)'
      : $tone === 'time'
      ? 'var(--ic-time-fg)'
      : 'var(--ic-doc-fg)'};

  svg {
    width: 19px;
    height: 19px;
  }
`;

export const CardTitles = styled.div`
  flex: 1 1 180px;
  min-width: 0;
`;

export const CardTitle = styled.div`
  font-size: 16.5px;
  font-weight: 700;
  letter-spacing: -0.015em;
`;

export const CardSub = styled.div`
  font-size: 12.5px;
  color: var(--ink-3);
  margin-top: 2px;
`;

export const CardHeadAside = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const CardBody = styled.div`
  padding: 0 22px 22px;
`;

/* ---------- fields ---------- */
export const Grid3 = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px 18px;

  @media (max-width: 1060px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

export const Fld = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

export const HintText = styled.div`
  font-size: 11.5px;
  color: var(--ink-4);
`;

/* ---------- program type index ---------- */
export const IdxPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  margin-top: 2px;
  padding: 4px 10px;
  border-radius: 7px;
  background: var(--accent-soft);
  color: var(--accent-ink);
  font-size: 12px;
  font-weight: 700;

  .k {
    font-weight: 600;
    opacity: 0.75;
    letter-spacing: 0.04em;
    font-size: 10px;
  }
  .v {
    font-size: 13px;
  }
`;

export const Note = styled.div`
  display: flex;
  gap: 11px;
  align-items: flex-start;
  margin-top: 16px;
  padding: 13px 16px;
  background: var(--accent-soft);
  border-radius: 10px;
  font-size: 13px;
  color: var(--accent-ink);
  line-height: 1.5;

  svg {
    width: 17px;
    height: 17px;
    flex: none;
    margin-top: 1px;
  }
`;

/* ---------- error banner ---------- */
export const Banner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 11px;
  font-size: 13px;
  line-height: 1.5;
  background: var(--req-soft);
  color: var(--req);
  border: 1px solid var(--req);

  svg {
    width: 17px;
    height: 17px;
    flex: none;
    margin-top: 2px;
  }
  b {
    font-weight: 700;
  }
  ul {
    margin: 6px 0 0;
    padding-left: 18px;
    max-height: 160px;
    overflow-y: auto;
  }
  li {
    margin-top: 2px;
  }
`;

/* ---------- toggle switch ---------- */
export const ToggleBtn = styled.button<{ $on?: boolean }>`
  position: relative;
  width: 42px;
  height: 24px;
  border-radius: 999px;
  border: 0;
  flex: none;
  cursor: pointer;
  padding: 0;
  background: ${({ $on }) => ($on ? 'var(--ok)' : '#d7dde8')};
  transition: background 0.18s ease;

  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: ${({ $on }) => ($on ? '21px' : '3px')};
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.28);
    transition: left 0.18s ease;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

export const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
`;

export const ToggleLabel = styled.div`
  font-size: 12.5px;
  font-weight: 600;
  color: var(--ink-2);
`;

export const ToggleSub = styled.div`
  font-size: 10.5px;
  color: var(--ink-4);
  line-height: 1.2;
`;

/* ---------- anchor multiselect ---------- */
export const Ms = styled.div`
  position: relative;
`;

export const MsBox = styled.div<{ $open?: boolean; $bad?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-height: 46px;
  width: 100%;
  padding: 7px 40px 7px 9px;
  border-radius: 9px;
  background: var(--field);
  border: 1px solid ${({ $open, $bad }) => ($bad ? 'var(--req)' : $open ? 'var(--focus)' : 'var(--line)')};
  box-shadow: ${({ $open }) => ($open ? '0 0 0 3px var(--accent-soft)' : 'none')};
  cursor: text;
  position: relative;

  &:hover {
    border-color: var(--ink-4);
  }
`;

export const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 5px 4px 10px;
  border-radius: 7px;
  background: var(--chip);
  border: 1px solid var(--chip-line);
  color: var(--chip-ink);
  font-size: 12.5px;
  font-weight: 600;

  button {
    width: 17px;
    height: 17px;
    border-radius: 5px;
    border: 0;
    background: none;
    color: inherit;
    cursor: pointer;
    display: grid;
    place-items: center;
    opacity: 0.65;
    padding: 0;

    &:hover {
      opacity: 1;
      background: #dde3ee;
    }
  }
`;

export const MsPh = styled.div`
  color: var(--ink-4);
  font-size: 14px;
  padding: 4px 2px;
`;

export const MsCaret = styled.button`
  position: absolute;
  right: 9px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  border: 0;
  background: none;
  color: var(--ink-3);
  cursor: pointer;
  display: grid;
  place-items: center;
  border-radius: 6px;

  &:hover {
    background: var(--ground);
    color: var(--ink);
  }
`;

export const MsPop = styled.div`
  position: absolute;
  z-index: 60;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 11px;
  box-shadow: var(--shadow-pop);
  overflow: hidden;
`;

export const MsSearch = styled.div`
  padding: 10px;
  border-bottom: 1px solid var(--line-2);
`;

export const MsList = styled.div`
  max-height: 236px;
  overflow-y: auto;
  padding: 6px;
`;

export const MsOpt = styled.button<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  border: 0;
  border-radius: 8px;
  background: none;
  cursor: pointer;
  text-align: left;
  font-size: 13.5px;
  color: var(--ink);

  &:hover {
    background: var(--ground);
  }

  .bx {
    width: 17px;
    height: 17px;
    border-radius: 5px;
    border: 1.5px solid ${({ $selected }) => ($selected ? 'var(--accent)' : 'var(--line)')};
    background: ${({ $selected }) => ($selected ? 'var(--accent)' : 'transparent')};
    flex: none;
    display: grid;
    place-items: center;
    color: #fff;
    font-size: 11px;
  }

  .role {
    margin-left: auto;
    font-size: 11px;
    color: var(--ink-4);
  }
`;

export const MsEmpty = styled.div`
  padding: 18px;
  text-align: center;
  font-size: 13px;
  color: var(--ink-4);
`;

/* ---------- schedule table ---------- */
export const Tbl = styled.div`
  border: 1px solid var(--line);
  border-radius: 11px;
  overflow: hidden;
`;

export const TRow = styled.div<{ $thead?: boolean; $off?: boolean }>`
  display: grid;
  grid-template-columns: 118px minmax(120px, 1fr) minmax(120px, 1fr) 148px 116px;
  gap: 14px;
  align-items: center;
  padding: 11px 16px;
  border-bottom: 1px solid var(--line-2);
  ${({ $thead }) =>
    $thead &&
    `
    background: var(--raise);
    font-size: 10.5px; font-weight: 700; letter-spacing: .09em; color: var(--ink-4);
  `}
  ${({ $off }) => $off && `color: var(--ink-3);`}

  &:last-child {
    border-bottom: 0;
  }

  @media (max-width: 1060px) {
    grid-template-columns: 104px minmax(100px, 1fr) minmax(100px, 1fr) 128px 104px;
    gap: 10px;
    padding: 11px 12px;
  }
`;

export const Day = styled.div`
  font-size: 13.5px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
`;

export const ScheduleFoot = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  margin-top: 13px;
  font-size: 12.5px;
  color: var(--ink-3);

  b {
    color: var(--ink-2);
    font-weight: 600;
  }
`;

export const DayModeBtn = styled.button<{ $off?: boolean }>`
  flex: none;
  height: 38px;
  padding: 0 11px;
  border-radius: 9px;
  border: 1px solid var(--line);
  background: var(--raise);
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: ${({ $off }) => ($off ? 'var(--ink-3)' : 'var(--ok-ink)')};

  &:hover {
    border-color: var(--ink-4);
  }
`;

export const Pulse = styled.span<{ $off?: boolean }>`
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ $off }) => ($off ? 'var(--ink-4)' : 'var(--ok)')};
`;

/* ---------- toast ---------- */
export const Toasts = styled.div`
  position: fixed;
  z-index: 200;
  left: 50%;
  transform: translateX(-50%);
  bottom: 22px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  pointer-events: none;
  width: max-content;
  max-width: 92vw;
`;

export const ToastEl = styled.div<{ $kind?: 'ok' | 'bad' }>`
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 11px 17px;
  border-radius: 10px;
  color: #fff;
  font-size: 13.5px;
  font-weight: 600;
  box-shadow: var(--shadow-pop);
  background: ${({ $kind }) => ($kind === 'bad' ? 'var(--req)' : $kind === 'ok' ? 'var(--ok)' : 'var(--ink)')};
  animation: ps-toast-rise 0.26s cubic-bezier(0.2, 0.8, 0.3, 1);

  @keyframes ps-toast-rise {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: none;
    }
  }
`;
